import React, { useState, MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CLOUDFRONT_ASSET_URL, INFO_SIGN } from '@osmosis/constants';
import { LinkObject, MenuTopic } from '../../types/topics';
import TopicsItemContainer from './TopicsItemContainer';
import { LabelComponent } from '../library/LabelComponent';
import { getSemesterLabels, hasNewLabel } from '../library/labelUtils';
import { P, H4, A } from '@osmosis/tailwind-ui/components/Text';
import { text5, textBlue, textPrimary } from '@osmosis/tailwind-ui/styles/text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { useEmbedContext } from '@osmosis/nextjs-components/context/EmbedProvider';

type Props = {
  item: MenuTopic;
  hasBgImage?: boolean;
  dataTestId?: string;
  isMobileView?: boolean;
  onSelectTopic?: (topic: LinkObject) => void;
  /** Heading level of the topic title; nests under the group name (5) by default. */
  headingLevel?: number;
};

const TopicName = ({ name = '', level }: { name: string; level: number }) => {
  const hasInfoSymbol = name.toLowerCase().includes(INFO_SIGN);

  const title = name.replace(new RegExp(INFO_SIGN, 'gi'), '');

  return (
    <>
      <H4
        aria-level={level}
        className={`inline min-w-fit text-sm ${textPrimary}`}
      >
        {title}
      </H4>
      {hasInfoSymbol ? (
        <span
          aria-hidden="true"
          data-testid="topics_L1_info_icon"
          className={`hover:text-primary-700 cursor-pointer pl-1.5 text-sm`}
        >
          {INFO_SIGN}
        </span>
      ) : null}
    </>
  );
};

/**
 * TopicsItem.
 */
const TopicsItem = (props: Props) => {
  const {
    item,
    hasBgImage,
    dataTestId,
    isMobileView = false,
    onSelectTopic,
    headingLevel = 6,
  } = props;

  const [maxLength, setMaxLength] = useState(13);
  const intl = useIntl();

  const handleMoreClick = async (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    setMaxLength(99);

    // The More button hides once expanded, so move focus to the first revealed topic.
    setTimeout(() => document.getElementById(`${path}_13`)?.focus(), 0);
  };

  const isButton = !!onSelectTopic;
  const entryClass = `${text5} hover:text-primary-700 w-full text-left no-underline`;
  const { targetAttribute } = useEmbedContext();

  return (
    <TopicsItemContainer
      isMobileView={isMobileView}
      displayName={item.displayName}
    >
      <Box data-testid="topics-item" className={`relative mx-4 w-full`}>
        {!isMobileView && (
          <Flex
            data-testid={`${dataTestId}_title`}
            className={`flex-nowrap items-center justify-start px-2 py-4`}
          >
            <TopicName name={item.displayName} level={headingLevel} />
          </Flex>
        )}

        {item.links?.slice(0, maxLength).map((el, idx) => {
          const onClickHandler = (e: MouseEvent) => {
            if (onSelectTopic) {
              e.preventDefault();
              e.stopPropagation();
              onSelectTopic(el);
            }
          };

          // Placement is driven by the label string value. See labelUtils:
          // "semester" tokens render above the name, "new" stays inline.
          const semesterLabels = getSemesterLabels(el.label);
          const showNewLabel = hasNewLabel(el.label);

          const entry = (
            <>
              {semesterLabels.map((semesterLabel, semesterIdx) => (
                <Box className="mb-1 px-2 pt-2" key={semesterLabel}>
                  <LabelComponent
                    content={semesterLabel}
                    dataTestId={
                      semesterIdx === 0
                        ? `semester-tag-${idx}`
                        : `semester-tag-${idx}-${semesterIdx}`
                    }
                    labelProps={{
                      style: {
                        backgroundColor: '#E9E3F9',
                        color: '#000000',
                        padding: '5px',
                      },
                    }}
                  />
                </Box>
              ))}
              <Box
                className={`min-w-auto rounded-md px-2 py-2 hover:bg-sky-600/25 lg:min-w-24`}
              >
                <Flex className={`items-center justify-between`}>
                  <Box className={`min-w-4/5 mr-2 text-sm`}>{el.name}</Box>

                  {showNewLabel && (
                    <LabelComponent
                      content={
                        <FormattedMessage
                          defaultMessage="NEW"
                          id="library.topics.list.item.tag.new"
                        />
                      }
                      dataTestId={`new-tag-${idx}`}
                      containerProps={{ style: { minWidth: '10%' } }}
                    />
                  )}
                </Flex>
              </Box>
            </>
          );

          return (
            <React.Fragment key={el.link}>
              {el.type === 'tile' ? (
                <Flex
                  className={`m-2 w-11/12 items-center justify-center rounded-md hover:cursor-pointer hover:bg-blue-200 hover:transition-all`}
                >
                  <P
                    data-testid="more-topics-coming-soon"
                    className={`hover:text-primary-700 p-2 text-xs font-bold hover:cursor-pointer`}
                  >
                    {el.name}
                  </P>
                </Flex>
              ) : (
                <Box
                  className={`break-words rounded-[5px] duration-300 hover:no-underline hover:transition-all`}
                >
                  {isButton ? (
                    <Button
                      variant="transparent"
                      color="transparent"
                      data-testid={`header_topic_${el.link}`}
                      onClick={onClickHandler}
                      id={`${item.path}_${idx}`}
                      className={`${entryClass} font-paragraph text-sm sm:text-base dark:text-blue-400 flex-col items-stretch justify-start rounded-none p-0 font-normal`}
                    >
                      {entry}
                    </Button>
                  ) : (
                    <A
                      data-testid={`header_topic_${el.link}`}
                      id={`${item.path}_${idx}`}
                      key={el.link}
                      href={el.link}
                      target={targetAttribute}
                      className={entryClass}
                    >
                      {entry}
                    </A>
                  )}
                </Box>
              )}
            </React.Fragment>
          );
        })}
        {item.links.length >= 13 && (
          <Button
            data-testid="more-btn"
            aria-label={intl.formatMessage(
              {
                id: 'components.topics-item.more_label',
                defaultMessage: 'More {topic}',
              },
              { topic: item.displayName },
            )}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              handleMoreClick(e, item.path);
            }}
            className={`cursor-pointer bg-transparent p-2 text-base font-normal ${textBlue} hover:bg-transparent ${
              item.links.length > maxLength ? '' : 'hidden'
            }`}
          >
            <FormattedMessage
              id="components.topics-item.more"
              defaultMessage="More..."
            />
          </Button>
        )}

        {hasBgImage && (
          <Box
            style={{
              background: `url(${CLOUDFRONT_ASSET_URL}9zlkeqWdRFuHt4zJXV9w64OqQqKETORf.png) no-repeat right bottom`,
              backgroundSize: 'contain',
            }}
            className={`absolute right-10 -top-12 h-10 w-12`}
          ></Box>
        )}
      </Box>
    </TopicsItemContainer>
  );
};

export default TopicsItem;
